import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';

/**
 * User Context - 일반 사용자 전용 컨텍스트
 * 사용자 인증 상태와 프로필 정보만 포함
 */
interface UserContextType {
  user: ReturnType<typeof useAuth>['user'];
  profile: ReturnType<typeof useProfile>['data'];
  isAuthenticated: boolean;
  isLoading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

/**
 * UserProvider - 일반 사용자 전용 컨텍스트 제공자
 * 최소한의 사용자 데이터만 관리하여 메모리 효율성 극대화
 */
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const { data: profile } = useProfile(user?.id || null);

  const value: UserContextType = {
    user,
    profile,
    isAuthenticated: !!user,
    isLoading: loading,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserProvider;